import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import './STUI.css';

// Layout components
import { Header, Sidebar, HomePage, Footer, AuthGate } from './components/layout';
import { AlertBar, DynamicModulePage, UnderConstruction, PermissionGuard, GlobalPermissionGuard, SysAdminGuard, AnyReportsPermGuard, AnyIntegrationPermGuard } from './components/common';
import { NotificationProvider } from './context/NotificationContext';
import { EntraAuthProvider, ImpersonationAuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import MsalProviderWrapper from './auth/MsalProviderWrapper';
import { isEntraAuth } from './auth/authConfig';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DatabasePickerPage from './pages/DatabasePickerPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';

// Development pages
import { SampleTableViewPage, TaskLogPage, DevPlanPage, SchemaViewerPage, FieldManagerPage, ClaudeNotesPage, RevLogPage, ListViewConfigPage, DevelopmentDashboardPage, IconBrowserPage, ImportTemplatePage, LookupValuesPage, DisplaySettingsPage, PowerQueryPage, SyncDashboardPage, ViewSyncPage, StyleGuidePage, DataEntryComposerPage, SchemaSyncPage, AdminConfigSyncPage, STUIPage } from './modules/development';

// Utilities pages
import { CombinedToolsPage, UtilitiesDashboardPage } from './modules/utilities';
import AuditTrailCrudPage from './modules/utilities/AuditTrailCrudPage';
import IoCountByPanelPage from './modules/utilities/IoCountByPanelPage';
import DistanceApproxPage from './modules/utilities/DistanceApproxPage';
import EstimateExportPage from './modules/utilities/EstimateExportPage';

// Integration pages
import { IntegrationDashboardPage, PlcPlatformsPage, PlcTagXRefPage, UdtCommentPage, ScadaTagGenerationPage, SimulationTagGenerationPage } from './modules/integration';

// Data Entry module pages
import { DataEntryModulePage, DataEntryDashboardPage } from './modules/data-entry';

// Supporting Documentation pages
import { SupportingDocsDashboardPage, SupportingDocsGroupPage } from './modules/supporting-docs';
import { LookupProvider } from './context/LookupContext';
import { DataTransferProvider } from './context/DataTransferContext';

// Activity + Preferences pages
import ActivityHistoryPage from './modules/activity/ActivityHistoryPage';
import PreferencesPage from './modules/preferences/PreferencesPage';

// Dashboard pages
import { DocumentationDashboardPage, NetworkingDashboardPage, EquipmentDashboardPage, SooDashboardPage } from './modules/dashboards';

// Report pages
import { ReportViewerPage, ReportsPage, OATCommissioningKeysPage } from './modules/reports';

// Administration pages
import { SecurityOverviewPage, SecurityDashboardPage, AdminUsersPage, RolesCapabilitiesPage, PermissionsPage, DatabasesPage, OrganizationsPage, SystemHealthPage, TableOwnershipPage, PreferenceDefaultsPage } from './modules/administration';

// Config
import { getPageDisplayName } from './config/navigationConfig';
import { adminManagementApi } from './services/api';
import { PreferencesProvider, getCachedPref, getLandingPath } from './context/PreferencesContext';

// Apply saved color theme immediately on module load — prevents flash of default styles
(function () {
  const t = localStorage.getItem('dawn.uiTheme');
  if (t && t !== 'standard') document.documentElement.classList.add(`dawn-theme-${t}`);
}());

// Pages that do NOT need a database slug (admin DB / dev tools)
const SLUG_FREE_PREFIXES = ['admin/', 'development-dashboard', 'sample-table-view', 'task-log', 'dev-plan', 'schema-viewer', 'field-manager', 'claude-notes', 'icon-browser', 'rev-log', 'standard-view-config', 'lookup-values', 'display-settings', 'database-sync', 'view-synchronizer', 'schema-synchronizer', 'admin-config-sync', 'style-guide', 'stui-style-guide', 'data-entry-view-config'];

const isSlugFreePage = (page) => SLUG_FREE_PREFIXES.some(p => page.startsWith(p));


/**
 * DbRedirect — routes bare "/" to the user's default database (server-backed
 * general.defaultDatabase preference, via the boot cache) or the picker.
 * Session slug is deliberately NOT a fallback — the default is an explicit
 * user choice, not "wherever you happened to be last".
 */
const DbRedirect = () => {
  const defaultDb = getCachedPref('general.defaultDatabase');
  if (defaultDb) return <Navigate to={getLandingPath(defaultDb)} replace />;
  return <Navigate to="/select-database" replace />;
};


/**
 * AdminLayout — shared layout for slug-free pages (admin + dev tools).
 * Uses Outlet to render the matched child route.
 */
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, accessDenied, loading: authLoading } = useAuth();

  const [currentPage, setCurrentPageState] = useState('');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pagePath = location.pathname.slice(1) || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  // Set browser title from active organization
  useEffect(() => {
    adminManagementApi.getOrganizations()
      .then(res => {
        const active = res.data.find(o => o.isActive) || res.data[0];
        if (active?.displayName) {
          document.title = `DAWN - ${active.displayName}`;
        }
      })
      .catch(() => {}); // silently fail — title stays as "DAWN - "
  }, []);

  // Navigate using router
  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      const lastSlug = localStorage.getItem('pmd-selected-db-slug') || getCachedPref('general.defaultDatabase');
      navigate(lastSlug ? `/${lastSlug}/${page === 'home' ? '' : page}` : '/select-database');
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  if (!authLoading && accessDenied) return <AccessDeniedPage />;

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => {
          const lastSlug = localStorage.getItem('pmd-selected-db-slug') || getCachedPref('general.defaultDatabase');
          navigate(lastSlug ? `/${lastSlug}/` : '/select-database');
        }}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/** Slug-aware redirect — preserves the current dbSlug in the target path. */
const SlugRedirect = ({ to }) => {
  const { dbSlug } = useDatabase();
  return <Navigate to={`/${dbSlug}/${to}`} replace />;
};

/**
 * AppContent — layout for data pages under /:dbSlug/*.
 */
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSysAdmin, accessDenied, currentUser, loading } = useAuth();
  const { dbSlug } = useDatabase();

  const [currentPage, setCurrentPageState] = useState('home');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const pagePath = pathParts.slice(1).join('/') || 'home';
    setCurrentPageState(pagePath);
    // Track last-visited page per slug — feeds the 'Last Visited' landing option
    if (dbSlug && pagePath !== 'home') localStorage.setItem(`dawn.lastPage.${dbSlug}`, pagePath);
  }, [location, dbSlug]);

  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      navigate(`/${dbSlug}/${page === 'home' ? '' : page}`);
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  const zeroRoles = !loading && !isSysAdmin && currentUser && Object.keys(currentUser?.roles || {}).length === 0;
  if (accessDenied || zeroRoles) {
    return <AccessDeniedPage />;
  }

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => setCurrentPage('home')}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />
            <Route path="/home" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />

            {/* Supporting Documentation dashboards */}
            <Route path="/supportingdocs-dashboard"    element={<SupportingDocsDashboardPage />} />
            <Route path="/supportingdocs-general"      element={<SupportingDocsGroupPage groupId="general" />} />
            <Route path="/supportingdocs-panels"       element={<SupportingDocsGroupPage groupId="panels" />} />
            <Route path="/supportingdocs-tags"         element={<SupportingDocsGroupPage groupId="tags" />} />
            <Route path="/supportingdocs-index"        element={<SupportingDocsGroupPage groupId="index" />} />
            <Route path="/supportingdocs-network"      element={<SupportingDocsGroupPage groupId="network" />} />
            <Route path="/supportingdocs-commissioning" element={<SupportingDocsGroupPage groupId="commissioning" />} />

            {/* Dynamic module routes - project DB data */}
            <Route path="/buildings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/building-column-distance/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/source/:recordId?" element={<DynamicModulePage />} />
            <Route path="/discipline/:recordId?" element={<DynamicModulePage />} />
            <Route path="/commissioning-key/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawing-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-sln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-dln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/organization/:recordId?" element={<DynamicModulePage />} />
            <Route path="/card-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-enet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-serial/:recordId?" element={<DynamicModulePage />} />
            <Route path="/plc-msg-map/:recordId?" element={<DynamicModulePage />} />
            <Route path="/tags/:recordId?" element={<DynamicModulePage />} />
            <Route path="/eng-units/:recordId?" element={<DynamicModulePage />} />
            <Route path="/isa-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/index-sheet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/manufacturer/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-terminal/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panels/:recordId?" element={<DynamicModulePage />} />
            <Route path="/power-distribution-dashboard" element={<SlugRedirect to="pwr-dist" />} />
            <Route path="/pwr-dist/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-index/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-equipment/:recordId?" element={<DynamicModulePage />} />
            <Route path="/subnet/:recordId?" element={<DynamicModulePage />} />
            {/* RFI Log page ripped 2026-08-05 — nav entry marked future.
                No route until we actually build it. */}
            <Route path="/system-code/:recordId?" element={<DynamicModulePage />} />
            <Route path="/transfer-party/:recordId?" element={<DynamicModulePage />} />
            <Route path="/valve-size/:recordId?" element={<DynamicModulePage />} />
            <Route path="/wire-type/:recordId?" element={<DynamicModulePage />} />

            {/* Data Entry module pages */}
            <Route path="/data-entry-dashboard" element={<DataEntryDashboardPage />} />
            <Route path="/commissioning"     element={<PermissionGuard permKey="dataentry.commissioning"><DataEntryModulePage moduleSlug="commissioning" /></PermissionGuard>} />
            <Route path="/estimating"        element={<PermissionGuard permKey="dataentry.estimating"><DataEntryModulePage moduleSlug="estimating" /></PermissionGuard>} />
            <Route path="/purchasing"        element={<PermissionGuard permKey="dataentry.purchasing"><DataEntryModulePage moduleSlug="purchasing" /></PermissionGuard>} />
            <Route path="/integration"       element={<PermissionGuard permKey="dataentry.integration"><DataEntryModulePage moduleSlug="integration" /></PermissionGuard>} />
            <Route path="/submittals"        element={<PermissionGuard permKey="dataentry.submittals"><DataEntryModulePage moduleSlug="submittals" /></PermissionGuard>} />
            <Route path="/sub-contractors"   element={<PermissionGuard permKey="dataentry.subcontractor"><DataEntryModulePage moduleSlug="sub-contractors" /></PermissionGuard>} />
            <Route path="/qaqc"              element={<PermissionGuard permKey="dataentry.qaqc"><DataEntryModulePage moduleSlug="qaqc" /></PermissionGuard>} />
            <Route path="/material-transfer" element={<PermissionGuard permKey="dataentry.materialtransfer"><DataEntryModulePage moduleSlug="material-transfer" /></PermissionGuard>} />

            {/* Activity + Preferences */}
            <Route path="/data-transfer-history" element={<ActivityHistoryPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />

            {/* Utilities */}
            <Route path="/utilities" element={<PermissionGuard permKey="utilities.bulkimport"><UtilitiesDashboardPage /></PermissionGuard>} />
            <Route path="/import-template" element={<PermissionGuard permKey="utilities.bulkimport"><ImportTemplatePage /></PermissionGuard>} />
            <Route path="/power-query" element={<PermissionGuard permKey="utilities.powerquery"><PowerQueryPage /></PermissionGuard>} />
            <Route path="/io-count-by-panel" element={<PermissionGuard permKey="utilities.iocount"><IoCountByPanelPage /></PermissionGuard>} />
            <Route path="/distance-approximation" element={<PermissionGuard permKey="utilities.distance"><DistanceApproxPage /></PermissionGuard>} />
            <Route path="/AudTrl-CRUD" element={<PermissionGuard permKey="utilities.audittrailcrud"><AuditTrailCrudPage /></PermissionGuard>} />
            <Route path="/estimate-export" element={<PermissionGuard permKey="utilities.estimateexport"><EstimateExportPage /></PermissionGuard>} />

            {/* Integration section — post 2026-08-05: each tool uses its own perm key */}
            <Route path="/integration-dashboard" element={<AnyIntegrationPermGuard><IntegrationDashboardPage /></AnyIntegrationPermGuard>} />
            <Route path="/io-buffer-generation" element={<PermissionGuard permKey="integration.iobuffer"><CombinedToolsPage /></PermissionGuard>} />

            <Route path="/plc-platforms" element={<PermissionGuard permKey="integration.plcplatforms"><PlcPlatformsPage /></PermissionGuard>} />
            <Route path="/plc-tag-xref" element={<PermissionGuard permKey="integration.tagxref"><PlcTagXRefPage /></PermissionGuard>} />
            <Route path="/udt-comment-generator" element={<PermissionGuard permKey="integration.udtcomment"><UdtCommentPage /></PermissionGuard>} />
            <Route path="/scada-tag-generation" element={<PermissionGuard permKey="integration.scadatagen"><ScadaTagGenerationPage /></PermissionGuard>} />
            <Route path="/simulation-tag-generation" element={<PermissionGuard permKey="integration.simtagen"><SimulationTagGenerationPage /></PermissionGuard>} />

            {/* Reports — Post 2026-08-05: per-section perms.
                Landing + generic viewer allowed if user has ANY reports.* perm;
                backend enforces per-report section on run. Hardcoded OAT
                page gated on its declared section (quality). */}
            <Route path="/reports" element={<AnyReportsPermGuard><ReportsPage /></AnyReportsPermGuard>} />
            <Route path="/reports/oat-commissioning-keys" element={<PermissionGuard permKey="reports.quality"><OATCommissioningKeysPage /></PermissionGuard>} />
            <Route path="/reports/:reportSlug" element={<AnyReportsPermGuard><ReportViewerPage /></AnyReportsPermGuard>} />
            <Route path="/documentation-dashboard" element={<DocumentationDashboardPage />} />
            <Route path="/networking-dashboard" element={<NetworkingDashboardPage />} />
            <Route path="/equipment-dashboard" element={<EquipmentDashboardPage />} />
            <Route path="/sequences-dashboard" element={<SooDashboardPage />} />

            {/* Catch-all */}
            <Route path="*" element={
              <UnderConstruction pageName={getPageDisplayName(location.pathname.split('/').slice(2).join('/'))} />
            } />
          </Routes>
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/**
 * StyleGuideHighlighter — detects ?sgHighlight=.some-class and adds a pulsing
 * outline to every matching element on the page. Zero impact unless the param is present.
 */
const StyleGuideHighlighter = () => {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [selector, setSelector] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlight = params.get('sgHighlight');
    if (!highlight) {
      setSelector(null);
      setCount(0);
      return;
    }

    setSelector(highlight);

    const applyHighlights = () => {
      try {
        const elements = document.querySelectorAll(highlight);
        elements.forEach(el => el.classList.add('sg-highlight'));
        setCount(elements.length);
      } catch (e) {
        // Invalid selector — ignore
      }
    };

    // Initial apply after a short delay for async renders
    const timer = setTimeout(applyHighlights, 300);

    // MutationObserver to catch elements rendered after initial load
    const observer = new MutationObserver(applyHighlights);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      // Clean up highlights
      document.querySelectorAll('.sg-highlight').forEach(el => el.classList.remove('sg-highlight'));
    };
  }, [location.search]);

  if (!selector) return null;

  return (
    <div className="sg-highlight-banner">
      <span>Style Guide — {count} instance{count !== 1 ? 's' : ''} of <code style={{ fontFamily: 'monospace' }}>{selector}</code> on this page</span>
      <button onClick={() => {
        document.querySelectorAll('.sg-highlight').forEach(el => el.classList.remove('sg-highlight'));
        setSelector(null);
        // Remove param from URL without page reload
        const url = new URL(window.location.href);
        url.searchParams.delete('sgHighlight');
        window.history.replaceState({}, '', url.toString());
      }}>✕ Dismiss</button>
    </div>
  );
};

/**
 * AppRoutes — all routes, rendered inside Router + Auth + Lookup providers.
 */
const AppRoutes = () => {
  return (
    <>
    <StyleGuideHighlighter />
    <Routes>
      <Route path="/" element={<DbRedirect />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/select-database" element={<DatabasePickerPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

      {/* Slug-free admin/dev pages — use AdminLayout with Outlet */}
      <Route element={<AdminLayout />}>
        {/* Admin: hardcoded SysAdmin — never permission-key driven so we can't lock ourselves out */}
        <Route path="/admin/dashboard" element={<SysAdminGuard><SecurityDashboardPage /></SysAdminGuard>} />
        <Route path="/admin/security" element={<SysAdminGuard><SecurityOverviewPage /></SysAdminGuard>} />
        <Route path="/admin/users" element={<SysAdminGuard><AdminUsersPage /></SysAdminGuard>} />
        <Route path="/admin/roles" element={<SysAdminGuard><RolesCapabilitiesPage /></SysAdminGuard>} />
        <Route path="/admin/permissions" element={<SysAdminGuard><PermissionsPage /></SysAdminGuard>} />
        <Route path="/admin/databases" element={<SysAdminGuard><DatabasesPage /></SysAdminGuard>} />
        <Route path="/admin/organizations" element={<SysAdminGuard><OrganizationsPage /></SysAdminGuard>} />
        <Route path="/admin/table-ownership" element={<SysAdminGuard><TableOwnershipPage /></SysAdminGuard>} />
        <Route path="/admin/preference-defaults" element={<SysAdminGuard><PreferenceDefaultsPage /></SysAdminGuard>} />
        <Route path="/admin/system-health" element={<SysAdminGuard><SystemHealthPage /></SysAdminGuard>} />
        <Route path="/admin/app-users" element={<SysAdminGuard><UnderConstruction pageName="Users" /></SysAdminGuard>} />
        {/* Dev tools: gated by development.main permission (global — not DB-scoped) */}
        <Route path="/development-dashboard" element={<GlobalPermissionGuard permKey="development.main"><DevelopmentDashboardPage /></GlobalPermissionGuard>} />
        <Route path="/sample-table-view" element={<GlobalPermissionGuard permKey="development.main"><SampleTableViewPage /></GlobalPermissionGuard>} />
        <Route path="/task-log" element={<GlobalPermissionGuard permKey="development.main"><TaskLogPage /></GlobalPermissionGuard>} />
        <Route path="/dev-plan" element={<GlobalPermissionGuard permKey="development.main"><DevPlanPage /></GlobalPermissionGuard>} />
        <Route path="/schema-viewer" element={<GlobalPermissionGuard permKey="development.main"><SchemaViewerPage /></GlobalPermissionGuard>} />
        <Route path="/field-manager" element={<GlobalPermissionGuard permKey="development.main"><FieldManagerPage /></GlobalPermissionGuard>} />
        <Route path="/claude-notes" element={<GlobalPermissionGuard permKey="development.main"><ClaudeNotesPage /></GlobalPermissionGuard>} />
        <Route path="/icon-browser" element={<GlobalPermissionGuard permKey="development.main"><IconBrowserPage /></GlobalPermissionGuard>} />
        <Route path="/rev-log" element={<GlobalPermissionGuard permKey="development.main"><RevLogPage /></GlobalPermissionGuard>} />
        <Route path="/standard-view-config" element={<GlobalPermissionGuard permKey="development.main"><ListViewConfigPage /></GlobalPermissionGuard>} />
        <Route path="/lookup-values" element={<GlobalPermissionGuard permKey="development.main"><LookupValuesPage /></GlobalPermissionGuard>} />
        <Route path="/display-settings" element={<GlobalPermissionGuard permKey="development.main"><DisplaySettingsPage /></GlobalPermissionGuard>} />
        <Route path="/database-sync" element={<GlobalPermissionGuard permKey="development.main"><SyncDashboardPage /></GlobalPermissionGuard>} />
        <Route path="/view-synchronizer" element={<GlobalPermissionGuard permKey="development.main"><ViewSyncPage /></GlobalPermissionGuard>} />
        <Route path="/schema-synchronizer" element={<GlobalPermissionGuard permKey="development.main"><SchemaSyncPage /></GlobalPermissionGuard>} />
        <Route path="/admin-config-sync" element={<GlobalPermissionGuard permKey="development.main"><AdminConfigSyncPage /></GlobalPermissionGuard>} />
        <Route path="/style-guide" element={<GlobalPermissionGuard permKey="development.main"><StyleGuidePage /></GlobalPermissionGuard>} />
        <Route path="/stui-style-guide" element={<GlobalPermissionGuard permKey="development.main"><STUIPage /></GlobalPermissionGuard>} />
        <Route path="/data-entry-view-config" element={<GlobalPermissionGuard permKey="development.main"><DataEntryComposerPage /></GlobalPermissionGuard>} />
      </Route>

      {/* Data pages — need DB slug */}
      <Route path="/:dbSlug/*" element={
        <DatabaseProvider>
          <AppContent />
        </DatabaseProvider>
      } />
    </Routes>
    </>
  );
};


// Apply stored UI zoom level on startup
const storedZoom = localStorage.getItem('dawn.uiZoom');
if (storedZoom && storedZoom !== '100%') {
  document.documentElement.style.zoom = storedZoom;
}

// Root App
const App = () => {
  if (isEntraAuth()) {
    return (
      <MsalProviderWrapper>
        <NotificationProvider>
          <EntraAuthProvider>
            <PreferencesProvider>
              <LookupProvider>
                <DataTransferProvider>
                  <Router>
                    <AuthGate>
                      <AppRoutes />
                    </AuthGate>
                  </Router>
                </DataTransferProvider>
              </LookupProvider>
            </PreferencesProvider>
          </EntraAuthProvider>
        </NotificationProvider>
      </MsalProviderWrapper>
    );
  }

  return (
    <NotificationProvider>
      <ImpersonationAuthProvider>
        <PreferencesProvider>
          <LookupProvider>
            <DataTransferProvider>
              <Router>
                <AuthGate>
                  <AppRoutes />
                </AuthGate>
              </Router>
            </DataTransferProvider>
          </LookupProvider>
        </PreferencesProvider>
      </ImpersonationAuthProvider>
    </NotificationProvider>
  );
};

export default App;
